import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, ShieldCheck, Trash2, Loader2, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { apiError } from "@/lib/format";

const PERMS = [
  { id: "READ", label: "Leitura", desc: "Dashboard e listagens" },
  { id: "OPERATOR", label: "Operador de Cadastro", desc: "Inscrição manual e status" },
  { id: "FINANCE", label: "Validador Financeiro", desc: "Validar pagamentos PIX" },
];

const empty = { name: "", email: "", password: "", role: "ADMIN_STAFF", permissions: ["READ"] };

export default function UsersAccess() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {});
  useEffect(() => { load(); }, []);

  const togglePerm = (u, perm) => {
    const has = (u.permissions || []).includes(perm);
    const next = has ? u.permissions.filter((p) => p !== perm) : [...(u.permissions || []), perm];
    api.patch(`/admin/users/${u.id}`, { permissions: next }).then(() => { toast.success("Permissões atualizadas"); load(); }).catch((e) => toast.error(apiError(e)));
  };

  const toggleActive = (u) => {
    api.patch(`/admin/users/${u.id}`, { active: !u.active }).then(() => { toast.success(u.active ? "Usuário desativado" : "Usuário ativado"); load(); }).catch((e) => toast.error(apiError(e)));
  };

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) return toast.error("Preencha nome, e-mail e senha");
    setSaving(true);
    try { await api.post("/admin/users", form); toast.success("Usuário criado!"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (u) => {
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Removido"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setPerm = (perm) => setForm((f) => ({ ...f, permissions: f.permissions.includes(perm) ? f.permissions.filter((p) => p !== perm) : [...f.permissions, perm] }));

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-users">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-brand font-bold uppercase tracking-widest text-xs">Controle de Acessos</p>
          <h1 className="font-display text-4xl font-black uppercase">Acessos (RBAC)</h1>
        </div>
        <Button data-testid="add-user-btn" onClick={() => { setForm(empty); setOpen(true); }} className="bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide">
          <Plus className="w-4 h-4 mr-1" /> Novo Usuário
        </Button>
      </div>

      <div className="space-y-4">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-card-${u.email}`} className="rounded-2xl bg-surface border border-white/10 p-5">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-brand/15 flex items-center justify-center">
                  {u.role === "SUPER_ADMIN" ? <ShieldCheck className="w-5 h-5 text-brand" /> : <UserCog className="w-5 h-5 text-neon" />}
                </div>
                <div>
                  <div className="font-semibold flex items-center gap-2">{u.name}
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${u.role === "SUPER_ADMIN" ? "bg-brand/20 text-brand" : "bg-neon/20 text-neon"}`}>{u.role === "SUPER_ADMIN" ? "Gestor Geral" : "Assistente"}</span>
                    {!u.active && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">Inativo</span>}
                  </div>
                  <div className="text-xs text-slate-400">{u.email}</div>
                </div>
              </div>
              {u.role !== "SUPER_ADMIN" && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 text-xs text-slate-400">Ativo <Switch data-testid={`user-active-${u.email}`} checked={u.active} onCheckedChange={() => toggleActive(u)} /></div>
                  <Button data-testid={`del-user-${u.email}`} size="icon" variant="ghost" className="h-8 w-8 hover:bg-red-500/10 text-red-400" onClick={() => del(u)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              )}
            </div>
            {u.role !== "SUPER_ADMIN" && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                {PERMS.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-xl bg-ink border border-white/10 p-3">
                    <div><div className="text-sm font-semibold">{p.label}</div><div className="text-[11px] text-slate-500">{p.desc}</div></div>
                    <Switch data-testid={`perm-${u.email}-${p.id}`} checked={(u.permissions || []).includes(p.id)} onCheckedChange={() => togglePerm(u, p.id)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Novo Assistente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-300">Nome *</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-ink border-white/10 mt-1" /></div>
            <div><Label className="text-slate-300">E-mail *</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="bg-ink border-white/10 mt-1" /></div>
            <div><Label className="text-slate-300">Senha *</Label><Input data-testid="user-password" type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="bg-ink border-white/10 mt-1" /></div>
            <div>
              <Label className="text-slate-300">Permissões</Label>
              <div className="space-y-2 mt-1">
                {PERMS.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-xl bg-ink border border-white/10 p-3">
                    <div><div className="text-sm font-semibold">{p.label}</div><div className="text-[11px] text-slate-500">{p.desc}</div></div>
                    <Switch data-testid={`new-perm-${p.id}`} checked={form.permissions.includes(p.id)} onCheckedChange={() => setPerm(p.id)} />
                  </div>
                ))}
              </div>
            </div>
            <Button data-testid="user-save-btn" onClick={create} disabled={saving} className="w-full bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar Usuário"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
